import { useEffect, useState } from 'react';
import {
  Book, Search, Filter, AlertCircle, AlertTriangle,
  Bell, Shield, Info, Check, X, Loader2,
} from 'lucide-react';
import { useArcStore } from '@/stores/arc-store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CidCode, CidEventCategory, AlarmEventSeverity } from '@asi/shared';
import { ArcSubNav } from '@/components/ArcSubNav';
import { cn } from '@/lib/utils';

// ─── Constants ────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<CidEventCategory, { label: string; color: string }> = {
  alarm: { label: 'Alarma', color: 'bg-red-500/10 text-red-500' },
  restore: { label: 'Restauración', color: 'bg-green-500/10 text-green-500' },
  open_close: { label: 'Apertura/Cierre', color: 'bg-blue-500/10 text-blue-500' },
  trouble: { label: 'Falla', color: 'bg-amber-500/10 text-amber-500' },
  test: { label: 'Prueba', color: 'bg-gray-500/10 text-gray-500' },
  system: { label: 'Sistema', color: 'bg-purple-500/10 text-purple-500' },
  bypass: { label: 'Bypass', color: 'bg-orange-500/10 text-orange-500' },
};

const SEVERITY_CONFIG: Record<AlarmEventSeverity, { label: string; color: string; icon: typeof AlertCircle }> = {
  CRITICAL: { label: 'Crítico', color: 'text-red-500', icon: AlertCircle },
  ALARM: { label: 'Alarma', color: 'text-orange-500', icon: AlertTriangle },
  WARNING: { label: 'Advertencia', color: 'text-amber-500', icon: Bell },
  INFO: { label: 'Informativo', color: 'text-blue-500', icon: Info },
};

// ─── CID Code Card ────────────────────────────────────────────

function CidCodeCard({ code }: { code: CidCode }) {
  const catConfig = CATEGORY_CONFIG[code.category];
  const sevConfig = SEVERITY_CONFIG[code.default_severity];
  const SeverityIcon = sevConfig.icon;

  return (
    <div className="flex items-start gap-4 p-4 border rounded-lg bg-card hover:bg-muted/50 transition-colors">
      {/* Code */}
      <div className="flex-shrink-0 text-center">
        <div className="text-2xl font-mono font-bold text-foreground">
          {code.code}
        </div>
        <SeverityIcon className={cn('h-5 w-5 mx-auto mt-1', sevConfig.color)} />
      </div>

      {/* Description */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-foreground">{code.description_es || code.description_en}</h3>
        {code.description_es && (
          <p className="text-sm text-muted-foreground mt-0.5">{code.description_en}</p>
        )}
        
        <div className="flex flex-wrap gap-2 mt-2">
          <Badge variant="secondary" className={cn('text-xs', catConfig.color)}>
            {catConfig.label}
          </Badge>
          <Badge variant="outline" className={cn('text-xs', sevConfig.color)}>
            {sevConfig.label}
          </Badge>
          {code.requires_response && (
            <Badge variant="secondary" className="text-xs bg-purple-500/10 text-purple-600">
              Requiere respuesta
            </Badge>
          )}
          {code.auto_dispatch && (
            <Badge variant="secondary" className="text-xs bg-red-500/10 text-red-600">
              Despacho automático
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function CidCodesPage() {
  const arcStore = useArcStore();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [severityFilter, setSeverityFilter] = useState<string>('ALL');
  const [requiresResponseFilter, setRequiresResponseFilter] = useState<string>('ALL');
  // Start in loading only if the store is empty; otherwise show cached data immediately
  const [loading, setLoading] = useState(arcStore.cidCodes.length === 0);

  useEffect(() => {
    arcStore.fetchCidCodes().finally(() => setLoading(false));
  }, []);

  // Filter codes
  const filteredCodes = arcStore.cidCodes.filter((code) => {
    if (categoryFilter !== 'ALL' && code.category !== categoryFilter) return false;
    if (severityFilter !== 'ALL' && code.default_severity !== severityFilter) return false;
    if (requiresResponseFilter === 'YES' && !code.requires_response) return false;
    if (requiresResponseFilter === 'NO' && code.requires_response) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        code.code.includes(search) ||
        code.description_en.toLowerCase().includes(searchLower) ||
        code.description_es?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Group by category
  const groupedCodes = filteredCodes.reduce((acc, code) => {
    const cat = code.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(code);
    return acc;
  }, {} as Record<CidEventCategory, CidCode[]>);

  const categoryOrder: CidEventCategory[] = [
    'alarm', 'restore', 'trouble', 'open_close', 'bypass', 'test', 'system'
  ];

  return (
    <div className="h-full flex flex-col p-6">
      <ArcSubNav />
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Book className="h-7 w-7" />
          Códigos CID (Contact ID)
        </h1>
        <p className="text-muted-foreground">
          Referencia de códigos estándar Ademco Contact ID para eventos de alarma
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código o descripción..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todas</SelectItem>
            {categoryOrder.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {CATEGORY_CONFIG[cat].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Severidad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todas</SelectItem>
            <SelectItem value="CRITICAL">Crítico</SelectItem>
            <SelectItem value="ALARM">Alarma</SelectItem>
            <SelectItem value="WARNING">Advertencia</SelectItem>
            <SelectItem value="INFO">Info</SelectItem>
          </SelectContent>
        </Select>

        <Select value={requiresResponseFilter} onValueChange={setRequiresResponseFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Respuesta" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            <SelectItem value="YES">Requiere respuesta</SelectItem>
            <SelectItem value="NO">Sin respuesta</SelectItem>
          </SelectContent>
        </Select>

        <Badge variant="secondary">
          {filteredCodes.length} códigos
        </Badge>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : arcStore.cidCodes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Book className="h-12 w-12 mb-4 opacity-50" />
            <p>No hay códigos CID configurados</p>
            <p className="text-sm mt-1">Ejecute la migración de datos semilla</p>
          </div>
        ) : categoryFilter !== 'ALL' ? (
          // Single category view
          <div className="grid gap-3 md:grid-cols-2">
            {filteredCodes.map((code) => (
              <CidCodeCard key={code.code} code={code} />
            ))}
          </div>
        ) : (
          // Grouped view
          <div className="space-y-8">
            {categoryOrder
              .filter((cat) => groupedCodes[cat]?.length > 0)
              .map((category) => (
                <div key={category}>
                  <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Badge className={cn('text-sm', CATEGORY_CONFIG[category].color)}>
                      {CATEGORY_CONFIG[category].label}
                    </Badge>
                    <span className="text-muted-foreground text-sm font-normal">
                      ({groupedCodes[category].length} códigos)
                    </span>
                  </h2>
                  <div className="grid gap-3 md:grid-cols-2">
                    {groupedCodes[category].map((code) => (
                      <CidCodeCard key={code.code} code={code} />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CidCodesPage;
